import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  TEXT_SECONDARY,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
  FONT_HEADING,
} from '../legacy-ball/table-management/designTokens';

// ── Types ────────────────────────────────────────────────────────────────────

interface KrioProverb {
  wisdomId: string;
  title: string;
  translation: string;
  interpretation: string;
}

interface StoryData {
  id: string;
  title: string;
  narrative: string;
  culturalInsight: string;
  krioProverbId?: string | null;
  heritageFact?: string | null;
  regenerationCount: number;
  status: string;
  krioProverb?: KrioProverb | null;
}

interface StoryGenerationResultProps {
  /** The session ID to generate a story for */
  sessionId: string;
  /** If a story already exists (from previous generation), pass it in */
  existingStory?: StoryData | null;
  /** Called when generation begins (to update parent state) */
  onGenerationStart?: () => void;
  /** Called when story is generated or updated */
  onStoryUpdate?: (story: StoryData) => void;
  /** Called when story is approved */
  onApproved?: () => void;
  /** Called when story is declined */
  onDeclined?: () => void;
}

type ViewState = 'loading' | 'result' | 'error' | 'edit';

const GENERATION_TIMEOUT_MS = 60000;
const MAX_REGENERATIONS = 3;

// ── Component ────────────────────────────────────────────────────────────────

export function StoryGenerationResult({
  sessionId,
  existingStory,
  onGenerationStart,
  onStoryUpdate,
  onApproved,
  onDeclined,
}: StoryGenerationResultProps) {
  const navigate = useNavigate();
  const [viewState, setViewState] = useState<ViewState>(existingStory ? 'result' : 'loading');
  const [story, setStory] = useState<StoryData | null>(existingStory || null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editNarrative, setEditNarrative] = useState('');
  const [editError, setEditError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const regenerationsRemaining = story ? MAX_REGENERATIONS - story.regenerationCount : MAX_REGENERATIONS;

  // ── Generate story on mount if no existing story ──────────────────────

  const generateStory = useCallback(async () => {
    setViewState('loading');
    setErrorMessage('');
    onGenerationStart?.();

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), GENERATION_TIMEOUT_MS);

      const result = await apiClient<StoryData>(
        `/story-builder/sessions/${sessionId}/generate`,
        { method: 'POST', signal: controller.signal },
      );

      clearTimeout(timeoutId);
      setStory(result);
      setViewState('result');
      onStoryUpdate?.(result);
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setErrorMessage('Story generation timed out. Please try again.');
      } else {
        setErrorMessage(
          err?.message || 'Story generation could not be completed. Please try again.',
        );
      }
      setViewState('error');
    }
  }, [sessionId, onGenerationStart, onStoryUpdate]);

  useEffect(() => {
    if (!existingStory) {
      generateStory();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ───────────────────────────────────────────────────────────

  const handleApprove = async () => {
    if (!story) return;
    setActionLoading(true);
    try {
      await apiClient(`/story-builder/stories/${story.id}/review`, {
        method: 'PATCH',
        body: { action: 'approve' },
      });
      onApproved?.();
      navigate('/story-builder/my-story');
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to approve story.');
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!story) return;
    setActionLoading(true);
    try {
      await apiClient(`/story-builder/stories/${story.id}/review`, {
        method: 'PATCH',
        body: { action: 'decline' },
      });
      onDeclined?.();
    } catch (err: any) {
      setErrorMessage(err?.message || 'Failed to decline story.');
      setActionLoading(false);
    }
  };

  const handleRegenerate = async () => {
    if (!story) return;
    setActionLoading(true);
    setErrorMessage('');
    setViewState('loading');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), GENERATION_TIMEOUT_MS);

      const result = await apiClient<StoryData>(
        `/story-builder/stories/${story.id}/regenerate`,
        { method: 'POST', signal: controller.signal },
      );

      clearTimeout(timeoutId);
      setStory(result);
      setViewState('result');
      onStoryUpdate?.(result);
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setErrorMessage('Story regeneration timed out. Please try again.');
      } else if (err?.statusCode === 429) {
        setErrorMessage('Maximum regeneration attempts reached.');
      } else {
        setErrorMessage(
          err?.message || 'Story could not be regenerated. The previous version is still available.',
        );
      }
      setViewState('result');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartEdit = () => {
    if (!story) return;
    setEditTitle(story.title);
    setEditNarrative(story.narrative);
    setEditError('');
    setViewState('edit');
  };

  const handleSaveEdit = async () => {
    if (!story) return;

    // Validate
    if (editNarrative.length < 1 || editNarrative.length > 2000) {
      setEditError('Narrative must be between 1 and 2000 characters.');
      return;
    }
    if (editTitle.length > 80) {
      setEditError('Title must be at most 80 characters.');
      return;
    }

    setActionLoading(true);
    setEditError('');

    try {
      const result = await apiClient<StoryData>(
        `/story-builder/stories/${story.id}/review`,
        {
          method: 'PATCH',
          body: { action: 'edit', narrative: editNarrative, title: editTitle },
        },
      );
      setStory({ ...story, ...result });
      setViewState('result');
      onStoryUpdate?.({ ...story, ...result });
    } catch (err: any) {
      setEditError(err?.message || 'Failed to save edits.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setViewState('result');
    setEditError('');
  };

  // ── Render: Loading State ──────────────────────────────────────────────

  if (viewState === 'loading') {
    return (
      <div style={styles.container}>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={styles.loadingCard}>
          <div style={styles.spinner} />
          <h2 style={styles.loadingTitle}>Crafting your story...</h2>
          <p style={styles.loadingSubtext}>
            Our AI is weaving your responses into a personalized narrative celebrating your
            connection to Krio heritage and the KDU community.
          </p>
        </div>
      </div>
    );
  }

  // ── Render: Error State ────────────────────────────────────────────────

  if (viewState === 'error') {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorTitle}>Generation Failed</h2>
          <p style={styles.errorMessage}>{errorMessage}</p>
          <button style={styles.retryButton} onClick={generateStory}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // ── Render: Edit State ─────────────────────────────────────────────────

  if (viewState === 'edit' && story) {
    return (
      <div style={styles.container}>
        <div style={styles.storyCard}>
          <h2 style={styles.sectionTitle}>Edit Your Story</h2>

          <div style={styles.editField}>
            <label style={styles.editLabel}>
              Title ({editTitle.length}/80)
            </label>
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              maxLength={80}
              style={styles.editInput}
              aria-label="Story title"
            />
          </div>

          <div style={styles.editField}>
            <label style={styles.editLabel}>
              Narrative ({editNarrative.length}/500)
            </label>
            <textarea
              value={editNarrative}
              onChange={(e) => setEditNarrative(e.target.value)}
              maxLength={500}
              rows={8}
              style={styles.editTextarea}
              aria-label="Story narrative"
            />
          </div>

          {editError && <p style={styles.editErrorText}>{editError}</p>}

          <div style={styles.editActions}>
            <button
              style={styles.approveButton}
              onClick={handleSaveEdit}
              disabled={actionLoading}
            >
              {actionLoading ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              style={styles.editButton}
              onClick={handleCancelEdit}
              disabled={actionLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Result State ───────────────────────────────────────────────

  if (!story || !story.title) {
    // Safety: if we got here without a valid story, show an error
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <div style={styles.errorIcon}>⚠️</div>
          <h2 style={styles.errorTitle}>Something went wrong</h2>
          <p style={styles.errorMessage}>Story data could not be loaded. Please try again.</p>
          <button style={styles.retryButton} onClick={generateStory}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.storyCard}>
        {/* Story Title */}
        <h2 style={styles.storyTitle}>{story.title}</h2>

        {/* Narrative */}
        <div style={styles.section}>
          <p style={styles.narrative}>{story.narrative}</p>
        </div>

        {/* Cultural Insight */}
        <div style={styles.insightSection}>
          <h3 style={styles.insightLabel}>Cultural Insight</h3>
          <p style={styles.insightText}>{story.culturalInsight}</p>
        </div>

        {/* Krio Proverb (if present) */}
        {story.krioProverb && (
          <div style={styles.proverbSection}>
            <h3 style={styles.proverbLabel}>🗣️ Krio Proverb</h3>
            <p style={styles.proverbKrio}>{story.krioProverb.title}</p>
            <p style={styles.proverbTranslation}>
              <em>"{story.krioProverb.translation}"</em>
            </p>
            <p style={styles.proverbInterpretation}>
              {story.krioProverb.interpretation}
            </p>
          </div>
        )}

        {/* Heritage Fact (if present) */}
        {story.heritageFact && (
          <div style={styles.heritageSection}>
            <h3 style={styles.heritageLabel}>📜 Heritage Fact</h3>
            <p style={styles.heritageText}>{story.heritageFact}</p>
          </div>
        )}

        {/* Error message (for action failures) */}
        {errorMessage && (
          <p style={styles.actionErrorText}>{errorMessage}</p>
        )}

        {/* Regeneration count */}
        <div style={styles.regenerationInfo}>
          <span style={styles.regenerationText}>
            {regenerationsRemaining} of {MAX_REGENERATIONS} regenerations remaining
          </span>
        </div>

        {/* Action Buttons */}
        <div style={styles.actions}>
          <button
            style={styles.approveButton}
            onClick={handleApprove}
            disabled={actionLoading}
            aria-label="Approve story for publication"
          >
            {actionLoading ? 'Processing...' : '✓ Approve'}
          </button>
          <button
            style={styles.regenerateButton}
            onClick={handleRegenerate}
            disabled={actionLoading || regenerationsRemaining <= 0}
            aria-label="Regenerate story"
          >
            ↻ Regenerate
          </button>
          <button
            style={styles.editButton}
            onClick={handleStartEdit}
            disabled={actionLoading}
            aria-label="Edit story"
          >
            ✏️ Edit
          </button>
          <button
            style={styles.declineButton}
            onClick={handleDecline}
            disabled={actionLoading}
            aria-label="Decline story publication"
          >
            Decline
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: {
    maxWidth: '700px',
    margin: '0 auto',
    padding: '1.5rem 1rem',
  },

  // Loading
  loadingCard: {
    background: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '12px',
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: `4px solid ${BORDER_LIGHT}`,
    borderTopColor: PRIMARY,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 1.5rem',
  },
  loadingTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.4rem',
    color: TEXT_DARK,
    margin: '0 0 0.75rem',
  },
  loadingSubtext: {
    color: TEXT_MUTED,
    fontSize: '0.95rem',
    lineHeight: '1.5',
    maxWidth: '400px',
    margin: '0 auto',
  },

  // Error
  errorCard: {
    background: BG_CARD,
    border: '1px solid #fecaca',
    borderRadius: '12px',
    padding: '3rem 2rem',
    textAlign: 'center',
  },
  errorIcon: {
    fontSize: '2.5rem',
    marginBottom: '1rem',
  },
  errorTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.3rem',
    color: '#991b1b',
    margin: '0 0 0.75rem',
  },
  errorMessage: {
    color: TEXT_SECONDARY,
    fontSize: '0.95rem',
    marginBottom: '1.5rem',
  },
  retryButton: {
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
  },

  // Story Card
  storyCard: {
    background: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '12px',
    padding: '2rem',
  },
  storyTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.5rem',
    color: TEXT_DARK,
    marginTop: 0,
    marginBottom: '1.25rem',
    lineHeight: 1.3,
  },
  section: {
    marginBottom: '1.5rem',
  },
  narrative: {
    color: TEXT_DARK,
    fontSize: '1rem',
    lineHeight: 1.7,
    margin: 0,
  },

  // Cultural Insight
  insightSection: {
    background: BG_SURFACE,
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    marginBottom: '1.25rem',
    borderLeft: `4px solid ${PRIMARY}`,
  },
  insightLabel: {
    fontFamily: FONT_HEADING,
    fontSize: '0.9rem',
    color: PRIMARY,
    margin: '0 0 0.5rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  insightText: {
    color: TEXT_DARK,
    fontSize: '0.95rem',
    lineHeight: 1.6,
    margin: 0,
  },

  // Proverb
  proverbSection: {
    background: '#fefbf3',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    marginBottom: '1.25rem',
    borderLeft: '4px solid #c9a84c',
  },
  proverbLabel: {
    fontFamily: FONT_HEADING,
    fontSize: '0.9rem',
    color: '#92740c',
    margin: '0 0 0.5rem',
    fontWeight: 600,
  },
  proverbKrio: {
    fontFamily: FONT_HEADING,
    fontSize: '1.05rem',
    color: TEXT_DARK,
    fontStyle: 'italic',
    margin: '0 0 0.4rem',
  },
  proverbTranslation: {
    color: TEXT_SECONDARY,
    fontSize: '0.9rem',
    margin: '0 0 0.4rem',
  },
  proverbInterpretation: {
    color: TEXT_MUTED,
    fontSize: '0.85rem',
    margin: 0,
    lineHeight: 1.5,
  },

  // Heritage Fact
  heritageSection: {
    background: '#f0fdf4',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    marginBottom: '1.25rem',
    borderLeft: `4px solid ${PRIMARY}`,
  },
  heritageLabel: {
    fontFamily: FONT_HEADING,
    fontSize: '0.9rem',
    color: PRIMARY,
    margin: '0 0 0.5rem',
    fontWeight: 600,
  },
  heritageText: {
    color: TEXT_DARK,
    fontSize: '0.9rem',
    lineHeight: 1.6,
    margin: 0,
  },

  // Regeneration info
  regenerationInfo: {
    textAlign: 'center',
    marginBottom: '1.25rem',
    paddingTop: '0.75rem',
    borderTop: `1px solid ${BORDER_LIGHT}`,
  },
  regenerationText: {
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },

  // Actions
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    justifyContent: 'center',
  },
  approveButton: {
    background: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1.25rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  regenerateButton: {
    background: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1.25rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  editButton: {
    background: 'transparent',
    color: TEXT_DARK,
    border: `1.5px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '0.7rem 1.25rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  declineButton: {
    background: 'transparent',
    color: '#dc2626',
    border: 'none',
    borderRadius: '8px',
    padding: '0.7rem 1.25rem',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
  },

  // Action error
  actionErrorText: {
    color: '#dc2626',
    fontSize: '0.85rem',
    textAlign: 'center',
    margin: '0 0 1rem',
  },

  // Edit mode
  sectionTitle: {
    fontFamily: FONT_HEADING,
    fontSize: '1.3rem',
    color: TEXT_DARK,
    margin: '0 0 1.5rem',
  },
  editField: {
    marginBottom: '1.25rem',
  },
  editLabel: {
    display: 'block',
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    marginBottom: '0.4rem',
    fontWeight: 500,
  },
  editInput: {
    width: '100%',
    padding: '0.6rem 0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.95rem',
    color: TEXT_DARK,
    boxSizing: 'border-box' as const,
  },
  editTextarea: {
    width: '100%',
    padding: '0.75rem',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '0.95rem',
    color: TEXT_DARK,
    lineHeight: 1.6,
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  editErrorText: {
    color: '#dc2626',
    fontSize: '0.85rem',
    margin: '0 0 1rem',
  },
  editActions: {
    display: 'flex',
    gap: '0.75rem',
    marginTop: '0.5rem',
  },
};
